import { useState } from "react";
import { MediaUpload } from "@/components/MediaUpload";
import { AIButton } from "@/components/AIButton";
import { LOCAL_CATEGORIES, APPLIANCE_PRESETS, GUIDE_THEMES, BRAND_COLORS } from "@/lib/sections";
import { Plus, Trash2, MapPin, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const inp = "w-full rounded-xl border border-slate-200 px-3.5 py-3 text-[15px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-shadow";
export const lbl = "block text-sm font-semibold text-slate-700 mb-1.5";

// Guide navigation sections (order matters for defaults)
export const GUIDE_SECTION_KEYS = ["welcome", "checkin", "checkout", "wifi", "house_guide", "house_rules", "parking", "local_guide", "getting_around", "emergency", "contact"];

// Recommended-completeness checklist
export function computeCompletion(prop) {
  if (!prop) return { percent: 0, missing: [] };
  const checks = [
    ["Cover photo", !!prop.branding?.cover],
    ["City / location", !!prop.basic?.city],
    ["Welcome message", !!prop.welcome_message],
    ["Check-in instructions", !!prop.checkin?.instructions],
    ["Check-out instructions", !!prop.checkout?.instructions],
    ["Wi-Fi information", !!prop.wifi?.network],
    ["House rules", !!(prop.house_rules && Object.entries(prop.house_rules).some(([k, v]) => k !== "custom" ? !!v : (v || []).length))],
    ["Parking information", !!(prop.parking?.availability || prop.parking?.instructions)],
    ["Local recommendations", (prop.local_guide || []).length > 0],
    ["Emergency numbers", !!prop.emergency?.local_numbers],
    ["Contact details", !!(prop.contact?.phone || prop.contact?.email)],
  ];
  const done = checks.filter(([, ok]) => ok).length;
  const percent = Math.round((done / checks.length) * 100);
  const missing = checks.filter(([, ok]) => !ok).map(([label]) => label);
  return { percent, missing };
}

export function EditorSection({ activeKey, prop, update, updateNested }) {
  const id = prop.id;
  const lang = prop.base_language || "en";

  switch (activeKey) {
    case "basic":
      return (
        <div className="space-y-4">
          <Field label="Property name"><input className={inp} value={prop.name} onChange={(e) => update({ name: e.target.value })} data-testid="f-name" /></Field>
          <Field label="Property type"><input className={inp} value={prop.basic.property_type} onChange={(e) => updateNested("basic", { property_type: e.target.value })} placeholder="Apartment, Villa…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><input className={inp} value={prop.basic.city} onChange={(e) => updateNested("basic", { city: e.target.value })} /></Field>
            <Field label="Country"><input className={inp} value={prop.basic.country} onChange={(e) => updateNested("basic", { country: e.target.value })} /></Field>
          </div>
          <Field label="Address"><input className={inp} value={prop.basic.address} onChange={(e) => updateNested("basic", { address: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max guests"><input type="number" inputMode="numeric" className={inp} value={prop.basic.max_guests ?? ""} onChange={(e) => updateNested("basic", { max_guests: e.target.value })} /></Field>
            <Field label="Bedrooms"><input type="number" inputMode="numeric" className={inp} value={prop.basic.bedrooms ?? ""} onChange={(e) => updateNested("basic", { bedrooms: e.target.value })} /></Field>
            <Field label="Beds"><input type="number" inputMode="numeric" className={inp} value={prop.basic.beds ?? ""} onChange={(e) => updateNested("basic", { beds: e.target.value })} /></Field>
            <Field label="Bathrooms"><input type="number" inputMode="numeric" className={inp} value={prop.basic.bathrooms ?? ""} onChange={(e) => updateNested("basic", { bathrooms: e.target.value })} /></Field>
          </div>
          <Field label={<span className="flex items-center justify-between w-full">Description <AIButton kind="description" context={{ language: lang, ...prop.basic, name: prop.name }} onResult={(t) => updateNested("basic", { description: t })} testid="ai-description" label="Write with AI" /></span>}>
            <textarea rows={4} className={inp} value={prop.basic.description} onChange={(e) => updateNested("basic", { description: e.target.value })} />
          </Field>
        </div>
      );
    case "photos":
      return (
        <div className="space-y-5">
          <Field label="Photos (cover & gallery)">
            <MediaUpload propertyId={id} category="gallery" gallery={prop.branding.gallery || []}
              coverPath={prop.branding.cover}
              onSetCover={(path) => updateNested("branding", { cover: path })}
              onChange={(g) => updateNested("branding", { gallery: g, cover: prop.branding.cover || g[0]?.storage_path })} />
            <p className="text-xs text-slate-400 mt-2">Tap the star on a photo to set it as the cover image.</p>
          </Field>
          <Field label="Guide template">
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(GUIDE_THEMES).map(([key, th]) => (
                <button key={key} type="button" onClick={() => updateNested("branding", { theme: key })} data-testid={`theme-${key}`}
                  className={`rounded-xl overflow-hidden border-2 transition-all ${prop.branding.theme === key ? "border-blue-500 ring-2 ring-blue-100" : "border-transparent"}`}>
                  <div className="h-14 w-full"><img src={th.img} alt={th.label} className="w-full h-full object-cover" /></div>
                  <div className="text-[11px] font-semibold py-1 text-slate-700 bg-white">{th.label}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Accent colour">
            <div className="flex items-center gap-2 flex-wrap">
              {BRAND_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => updateNested("branding", { brand_color: c })} data-testid={`brand-${c}`}
                  className={`w-9 h-9 rounded-full border-2 transition-transform ${prop.branding.brand_color === c ? "border-slate-900 scale-110" : "border-white shadow"}`} style={{ background: c }} />
              ))}
              <input type="color" value={prop.branding.brand_color} onChange={(e) => updateNested("branding", { brand_color: e.target.value })} className="w-9 h-9 rounded-full border border-slate-200 cursor-pointer p-0" data-testid="f-brandcolor" />
            </div>
          </Field>
        </div>
      );
    case "welcome":
      return (
        <Field label={<span className="flex items-center justify-between w-full">Welcome message <AIButton kind="welcome_message" context={{ language: lang, name: prop.name, city: prop.basic.city }} onResult={(t) => update({ welcome_message: t })} testid="ai-welcome" label="Write with AI" /></span>}>
          <textarea rows={6} className={inp} value={prop.welcome_message} onChange={(e) => update({ welcome_message: e.target.value })} data-testid="f-welcome" placeholder="A warm greeting your guests see first…" />
        </Field>
      );
    case "checkin":
      return (
        <div className="space-y-4">
          <Field label="Check-in time"><input className={inp} value={prop.checkin.time} onChange={(e) => updateNested("checkin", { time: e.target.value })} placeholder="15:00" /></Field>
          <Field label={<span className="flex items-center justify-between w-full">Instructions <AIButton kind="checkin_instructions" context={{ language: lang, name: prop.name, address: prop.basic.address }} onResult={(t) => updateNested("checkin", { instructions: t })} testid="ai-checkin" label="Write with AI" /></span>}>
            <textarea rows={5} className={inp} value={prop.checkin.instructions} onChange={(e) => updateNested("checkin", { instructions: e.target.value })} />
          </Field>
        </div>
      );
    case "checkout":
      return (
        <div className="space-y-4">
          <Field label="Check-out time"><input className={inp} value={prop.checkout.time} onChange={(e) => updateNested("checkout", { time: e.target.value })} placeholder="11:00" /></Field>
          <Field label={<span className="flex items-center justify-between w-full">Instructions <AIButton kind="checkout_instructions" context={{ language: lang, name: prop.name }} onResult={(t) => updateNested("checkout", { instructions: t })} testid="ai-checkout" label="Write with AI" /></span>}>
            <textarea rows={5} className={inp} value={prop.checkout.instructions} onChange={(e) => updateNested("checkout", { instructions: e.target.value })} />
          </Field>
        </div>
      );
    case "wifi":
      return (
        <div className="space-y-4">
          <Field label="Network name"><input className={inp} value={prop.wifi.network} onChange={(e) => updateNested("wifi", { network: e.target.value })} data-testid="f-wifi-net" /></Field>
          <Field label="Password"><input className={inp} value={prop.wifi.password} onChange={(e) => updateNested("wifi", { password: e.target.value })} data-testid="f-wifi-pw" /></Field>
        </div>
      );
    case "access":
      return (
        <div className="space-y-4">
          <Field label="Building entrance"><textarea rows={2} className={inp} value={prop.access.building_entrance} onChange={(e) => updateNested("access", { building_entrance: e.target.value })} /></Field>
          <Field label="Door instructions"><textarea rows={2} className={inp} value={prop.access.door_instructions} onChange={(e) => updateNested("access", { door_instructions: e.target.value })} /></Field>
          <Field label="Keybox / smart lock"><textarea rows={2} className={inp} value={prop.access.keybox} onChange={(e) => updateNested("access", { keybox: e.target.value })} /></Field>
          <Field label="Access codes"><input className={inp} value={prop.access.access_codes} onChange={(e) => updateNested("access", { access_codes: e.target.value })} /></Field>
        </div>
      );
    case "house_rules": {
      const hr = prop.house_rules;
      const setHr = (patch) => updateNested("house_rules", patch);
      return (
        <div className="space-y-4">
          {[["smoking", "Smoking"], ["pets", "Pets"], ["noise", "Noise"], ["parties", "Parties"], ["visitors", "Visitors"]].map(([k, l]) => (
            <Field key={k} label={l}><input className={inp} value={hr[k]} onChange={(e) => setHr({ [k]: e.target.value })} /></Field>
          ))}
          <ListEditor label="Custom rules" items={hr.custom || []} onChange={(custom) => setHr({ custom })} placeholder="Add a custom rule" />
        </div>
      );
    }
    case "parking":
      return (
        <div className="space-y-4">
          <Field label="Availability"><input className={inp} value={prop.parking.availability} onChange={(e) => updateNested("parking", { availability: e.target.value })} placeholder="Free street parking, Paid garage…" /></Field>
          <Field label="Instructions"><textarea rows={3} className={inp} value={prop.parking.instructions} onChange={(e) => updateNested("parking", { instructions: e.target.value })} /></Field>
          <Field label="Cost"><input className={inp} value={prop.parking.cost} onChange={(e) => updateNested("parking", { cost: e.target.value })} /></Field>
        </div>
      );
    case "house_guide":
      return (
        <RepeaterEditor items={prop.house_guide || []} onChange={(house_guide) => update({ house_guide })}
          propertyId={id} category="house-guide" lang={lang} propName={prop.name}
          aiKind="appliance_instructions" presets={APPLIANCE_PRESETS}
          fields={[{ key: "name", label: "Feature", type: "preset" }, { key: "instructions", label: "Instructions", type: "textarea", ai: true }]}
          emptyItem={{ name: "", instructions: "", photo: null }} addLabel="Add appliance / feature" />
      );
    case "local_guide":
      return (
        <RepeaterEditor items={prop.local_guide || []} onChange={(local_guide) => update({ local_guide })}
          propertyId={id} category="local-recommendations" lang={lang} propName={prop.name}
          aiKind="local_recommendation" categories={LOCAL_CATEGORIES}
          fields={[
            { key: "name", label: "Place name", type: "text" },
            { key: "category", label: "Category", type: "select" },
            { key: "description", label: "Your recommendation", type: "textarea", ai: true },
            { key: "distance", label: "Distance", type: "text" },
            { key: "maps_link", label: "Google Maps link", type: "text" },
          ]}
          emptyItem={{ name: "", category: "Restaurant", description: "", distance: "", maps_link: "", photo: null }} addLabel="Add recommendation" />
      );
    case "getting_around":
      return <MobilityEditor prop={prop} update={update} updateNested={updateNested} />;
    case "emergency":
      return (
        <div className="space-y-4">
          <Field label="Local emergency number"><input className={inp} value={prop.emergency.local_numbers} onChange={(e) => updateNested("emergency", { local_numbers: e.target.value })} placeholder="112" /></Field>
          <Field label="Host contact"><input className={inp} value={prop.emergency.host_contact} onChange={(e) => updateNested("emergency", { host_contact: e.target.value })} /></Field>
          <Field label="Emergency contact"><input className={inp} value={prop.emergency.emergency_contact} onChange={(e) => updateNested("emergency", { emergency_contact: e.target.value })} /></Field>
          <Field label="Nearest hospital"><input className={inp} value={prop.emergency.hospital} onChange={(e) => updateNested("emergency", { hospital: e.target.value })} /></Field>
          <Field label="Nearest pharmacy"><input className={inp} value={prop.emergency.pharmacy} onChange={(e) => updateNested("emergency", { pharmacy: e.target.value })} /></Field>
        </div>
      );
    case "contact":
      return (
        <div className="space-y-4">
          <Field label="Host name"><input className={inp} value={prop.contact.host_name} onChange={(e) => updateNested("contact", { host_name: e.target.value })} /></Field>
          <Field label="Phone"><input className={inp} value={prop.contact.phone} onChange={(e) => updateNested("contact", { phone: e.target.value })} /></Field>
          <Field label="WhatsApp (with country code)"><input className={inp} value={prop.contact.whatsapp} onChange={(e) => updateNested("contact", { whatsapp: e.target.value })} placeholder="+39…" /></Field>
          <Field label="Email"><input className={inp} type="email" value={prop.contact.email} onChange={(e) => updateNested("contact", { email: e.target.value })} /></Field>
        </div>
      );
    default:
      return null;
  }
}

export const Field = ({ label, children }) => (<div><label className={lbl}>{label}</label>{children}</div>);

function MobilityEditor({ prop, update, updateNested }) {
  const [loading, setLoading] = useState(false);
  const geo = prop.geo;
  const query = [prop.basic?.address, prop.basic?.city, prop.basic?.country].filter(Boolean).join(", ");

  const locate = async () => {
    if (!query.trim()) { toast.error("Inserisci prima l'indirizzo nella sezione «Dati struttura»."); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/geocode", { params: { q: query } });
      if (data.ok) { update({ geo: { lat: data.lat, lng: data.lng } }); toast.success("Posizione trovata e salvata sulla mappa."); }
      else toast.error("Indirizzo non trovato. Prova a essere più specifico.");
    } catch { toast.error("Impossibile cercare la posizione ora."); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="Indirizzo">
        <input className={inp} value={prop.basic?.address || ""} onChange={(e) => updateNested("basic", { address: e.target.value })} placeholder="Via, numero civico, città" data-testid="f-address" />
      </Field>
      <button type="button" onClick={locate} disabled={loading} data-testid="geocode-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 py-3 text-sm font-semibold text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Trova sulla mappa
      </button>
      {geo?.lat != null && (
        <div className="rounded-2xl overflow-hidden border border-slate-200">
          <div className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-emerald-600 bg-emerald-50"><Check className="w-3.5 h-3.5" /> Posizione salvata</div>
          <iframe title="Anteprima mappa" data-testid="geocode-preview" className="w-full h-40 border-0" loading="lazy"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lng - 0.008}%2C${geo.lat - 0.006}%2C${geo.lng + 0.008}%2C${geo.lat + 0.006}&layer=mapnik&marker=${geo.lat}%2C${geo.lng}`} />
        </div>
      )}
      <Field label="Note sui trasporti (facoltative)">
        <textarea rows={5} className={inp} value={prop.getting_around || ""} onChange={(e) => update({ getting_around: e.target.value })} placeholder="Es. La metro più vicina è a 5 minuti a piedi, la linea 3 porta al centro…" data-testid="f-getting-around" />
      </Field>
      <p className="text-xs text-slate-400">Gli ospiti vedranno la mappa e apriranno Google Maps per orari e percorsi reali. Non generiamo automaticamente fermate o orari.</p>
    </div>
  );
}

function ListEditor({ label, items, onChange, placeholder }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <Field label={label}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input className={inp} value={it} onChange={(e) => onChange(items.map((x, j) => j === i ? e.target.value : x))} />
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input className={inp} value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <button onClick={add} className="p-2.5 rounded-xl bg-blue-50 text-blue-600"><Plus className="w-4 h-4" /></button>
        </div>
      </div>
    </Field>
  );
}

function RepeaterEditor({ items, onChange, propertyId, category, fields, emptyItem, addLabel, aiKind, lang, propName, presets, categories }) {
  const setItem = (i, patch) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  const removeItem = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid={`repeater-item-${i}`}>
          <div className="flex justify-end"><button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>
          {fields.map((f) => (
            <Field key={f.key} label={f.ai
              ? <span className="flex items-center justify-between w-full">{f.label} <AIButton kind={aiKind} context={{ language: lang, name: it.name, category: it.category, property: propName }} onResult={(t) => setItem(i, { [f.key]: t })} label="Write with AI" /></span>
              : f.label}>
              {f.type === "textarea" ? (
                <textarea rows={3} className={inp} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })} />
              ) : f.type === "select" ? (
                <select className={inp} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })}>
                  {(categories || []).map((c) => <option key={c}>{c}</option>)}
                </select>
              ) : f.type === "preset" ? (
                <input className={inp} list={`presets-${category}`} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })} />
              ) : (
                <input className={inp} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })} />
              )}
            </Field>
          ))}
          <Field label="Photo">
            <MediaUpload propertyId={propertyId} category={category} multiple={false}
              gallery={it.photo ? [{ id: "p", storage_path: it.photo, url: it.photo }] : []}
              onChange={(g) => setItem(i, { photo: g[g.length - 1]?.storage_path || null })} />
          </Field>
        </div>
      ))}
      {presets && <datalist id={`presets-${category}`}>{presets.map((p) => <option key={p} value={p} />)}</datalist>}
      <button onClick={() => onChange([...items, { ...emptyItem }])} data-testid="repeater-add" className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-3 text-sm font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600">
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}
